import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { get, post, put } from '../api';
import { useMe } from '../auth';
import {
  Alert,
  Card,
  CopyButton,
  Empty,
  fmtDate,
  Modal,
  PageHeader,
  Spinner,
  StatusBadge,
  Toggle,
  useAction,
  useLoader,
} from '../components/ui';
import type { Settings, WebhookDelivery, WebhookTestResult } from '../types';

export function WebhookPage() {
  const { isAdmin } = useMe();
  const { data: settings, error, setData } = useLoader(() => get<Settings>('/api/settings'), []);
  const deliveries = useLoader(() => get<{ deliveries: WebhookDelivery[] }>('/api/webhook-deliveries'), []);
  const act = useAction();

  const [url, setUrl] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [reveal, setReveal] = useState(false);
  const [saved, setSaved] = useState(false);
  const [test, setTest] = useState<WebhookTestResult | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [viewing, setViewing] = useState<WebhookDelivery | null>(null);

  useEffect(() => {
    if (settings) {
      setUrl(settings.webhook_url ?? '');
      setEnabled(settings.webhook_enabled);
    }
  }, [settings]);

  const dirty = !!settings && (url.trim() !== (settings.webhook_url ?? '') || enabled !== settings.webhook_enabled);

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setSaved(false);
    const res = await act.run('save', () =>
      put<Settings>('/api/settings', { webhook_url: url.trim() || null, webhook_enabled: enabled }),
    );
    if (res) {
      setData(res);
      setSaved(true);
    }
  };
  const rotate = async () => {
    if (!window.confirm('Rotate the signing secret? Your endpoint must use the new secret immediately.')) return;
    const res = await act.run('rotate', () => post<{ webhook_secret: string }>('/api/settings/webhook/rotate-secret'));
    if (res && settings) {
      setData({ ...settings, webhook_secret: res.webhook_secret });
      setReveal(true);
    }
  };
  const sendTest = async () => {
    setTest(null);
    const res = await act.run('test', () => post<WebhookTestResult>('/api/settings/webhook/test'));
    if (res) {
      setTest(res);
      void deliveries.reload(true);
    }
  };
  const retry = (d: WebhookDelivery) =>
    act.run(`retry-${d.id}`, async () => {
      await post(`/api/webhook-deliveries/${d.id}/retry`);
      await deliveries.reload(true);
      setTimeout(() => void deliveries.reload(true), 2000);
    });

  const list = (deliveries.data?.deliveries ?? []).filter((d) => !statusFilter || d.status === statusFilter);
  const secret = settings?.webhook_secret;

  return (
    <>
      <PageHeader title="Webhook" subtitle="MsgGuard POSTs every moderation decision to your endpoint." />
      <Alert>{error ?? act.error}</Alert>
      {!settings ? (
        !error && <Spinner />
      ) : (
        <>
          <Card title="Endpoint">
            <form className="form" onSubmit={save}>
              <label className="field">
                <span>Endpoint URL</span>
                <input
                  type="url"
                  placeholder="https://example.com/msgguard/webhook"
                  value={url}
                  disabled={!isAdmin}
                  onChange={(e) => {
                    setUrl(e.target.value);
                    setSaved(false);
                  }}
                />
                <span className="hint">
                  Events: <code>message.moderated</code> for every message (allowed ones too), <code>message.reviewed</code> when someone
                  sets a decision on a message page, <code>message.failed</code>, <code>user.unblocked</code> and <code>webhook.test</code>.
                </span>
              </label>
              <Toggle checked={enabled} disabled={!isAdmin} onChange={(v) => { setEnabled(v); setSaved(false); }} label="Deliveries enabled" />
              {isAdmin && (
                <div className="btn-row">
                  <button className="btn btn-primary" disabled={!dirty || act.busy === 'save'}>
                    {act.busy === 'save' ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    type="button"
                    className="btn"
                    disabled={dirty || !settings.webhook_url || !settings.webhook_enabled || act.busy === 'test'}
                    onClick={sendTest}
                    title={dirty ? 'Save first' : undefined}
                  >
                    {act.busy === 'test' ? 'Sending…' : 'Send test event'}
                  </button>
                  {saved && <span className="tone-text-ok small">Saved</span>}
                </div>
              )}
              {test && (
                <Alert kind={test.status === 'success' ? 'success' : 'error'}>
                  Test delivery {test.status === 'success' ? 'succeeded' : 'failed'}
                  {test.last_status_code != null && <> · HTTP {test.last_status_code}</>}
                  {test.last_error && <> · {test.last_error}</>}
                </Alert>
              )}
            </form>
          </Card>

          <Card title="Signing secret" subtitle="Used to compute the x-msgguard-signature header (HMAC-SHA256).">
            {secret ? (
              <div className="secret-row">
                <code className="mono secret-value">{reveal ? secret : `${secret.slice(0, 6)}${'•'.repeat(24)}`}</code>
                <button className="btn btn-sm" onClick={() => setReveal((r) => !r)}>
                  {reveal ? 'Hide' : 'Reveal'}
                </button>
                <CopyButton text={secret} />
                {isAdmin && (
                  <button className="btn btn-sm btn-danger-ghost" disabled={act.busy === 'rotate'} onClick={rotate}>
                    Rotate
                  </button>
                )}
              </div>
            ) : (
              <p className="muted">Only admins can see the signing secret.</p>
            )}
            <p className="small muted">
              Header format: <code>t=&lt;unix&gt;,v1=&lt;hex hmac-sha256 of "&lt;t&gt;.&lt;raw body&gt;"&gt;</code>. See{' '}
              <Link to="/keys">API keys → Integration</Link> for a verification snippet.
            </p>
          </Card>

          <Card
            title="Recent deliveries"
            subtitle="Last 100 webhook requests"
            className="card-flush"
            actions={
              <div className="btn-row">
                <select className="select-sm" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} aria-label="Filter by status">
                  <option value="">All statuses</option>
                  <option value="success">Success</option>
                  <option value="pending">Pending</option>
                  <option value="failed">Failed</option>
                </select>
                <button className="btn btn-sm" onClick={() => deliveries.reload()}>
                  Refresh
                </button>
              </div>
            }
          >
            {deliveries.loading && !deliveries.data ? (
              <Spinner />
            ) : list.length === 0 ? (
              <Empty>No deliveries{statusFilter ? ` with status ${statusFilter}` : ' yet'}.</Empty>
            ) : (
              <div className="table-wrap">
                <table className="table table-compact">
                  <thead>
                    <tr>
                      <th>Created</th>
                      <th>Event</th>
                      <th>Status</th>
                      <th className="num">Attempts</th>
                      <th>Response</th>
                      <th>Message</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((d) => (
                      <tr key={d.id}>
                        <td className="nowrap muted">{fmtDate(d.created_at, true)}</td>
                        <td className="mono nowrap">{d.event}</td>
                        <td>
                          <StatusBadge status={d.status} />
                        </td>
                        <td className="num">{d.attempts}</td>
                        <td className="small">
                          {d.last_status_code != null && <span className="mono">{d.last_status_code} </span>}
                          {d.last_error && <span className="tone-text-bad">{d.last_error}</span>}
                        </td>
                        <td>{d.message_id ? <Link to={`/messages/${d.message_id}`}>view</Link> : <span className="muted">—</span>}</td>
                        <td className="right nowrap">
                          <button className="btn btn-sm btn-ghost" onClick={() => setViewing(d)}>
                            Payload
                          </button>
                          {isAdmin && d.status === 'failed' && (
                            <button className="btn btn-sm" disabled={act.busy === `retry-${d.id}`} onClick={() => retry(d)}>
                              Retry
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}
      {viewing && (
        <Modal title={`${viewing.event} payload`} onClose={() => setViewing(null)} wide>
          <p className="small muted mono">{viewing.url}</p>
          <pre className="json">{JSON.stringify(viewing.payload, null, 2)}</pre>
        </Modal>
      )}
    </>
  );
}
